import {ExportToCsv} from 'export-to-csv';
import moment from 'moment';
import React from 'react';

import * as Remote from 'app/modules/Remote';
import {ApiInvoice, ApiPaidLayerInvoiceLineItem} from 'app/modules/Remote/Organization';
import {
  ApiImageryContractWithProjects,
  ApiImageryPurchaseBillingRecord,
} from 'app/modules/Remote/Project';
import * as costUtils from 'app/utils/costUtils';
import * as imageryUtils from 'app/utils/imageryUtils';

export const formatInvoiceDueDate = (invoice: ApiInvoice) => {
  const dateField =
    invoice['collection_method'] === 'charge_automatically' ? 'next_payment_attempt' : 'due_date';
  return `${moment.unix(invoice[dateField]).format('MMMM Do')}`;
};

export function formatLineItem(lineItem: ApiPaidLayerInvoiceLineItem) {
  return (
    <li style={{display: 'flex', justifyContent: 'space-between'}}>
      <div>{lineItem.description}</div>
      <div>{costUtils.formatCost(lineItem.amount)}</div>
    </li>
  );
}

export const appendBillingPeriodsToBillingRecords = async (
  billingRecords: ApiImageryPurchaseBillingRecord[],
  api: Pick<typeof Remote.api, 'organizations'>,
  organizationId: string
): Promise<(ApiImageryPurchaseBillingRecord & {billingPeriod: imageryUtils.BillingPeriod})[]> => {
  const invoices = billingRecords.some((r) => r.imageryContractId !== null)
    ? (await api.organizations.invoices(organizationId)).toJS()
    : [];

  const invoiceMap = invoices.reduce((acc, invoice) => {
    acc[invoice.id] = invoice;
    return acc;
  }, {});

  const purchasesWithBillingPeriods = billingRecords.map((billingRecord) => {
    // A purchase paid at checkout is billed on its own invoice the day it's ordered.
    if (billingRecord.imageryContractId === null) {
      const orderDate = moment(billingRecord.purchasedAt).format('MMM DD, YYYY');
      return {...billingRecord, billingPeriod: {start: orderDate, end: orderDate}};
    }
    const invoice = invoiceMap[billingRecord.invoiceId];
    if (
      !invoice ||
      billingRecord.invoiceId === 'unknown' ||
      !invoice.period_start ||
      !invoice.period_end
    ) {
      return {
        ...billingRecord,
        billingPeriod: {
          start: 'N/A',
          end: 'N/A',
        },
      };
    }
    const billingPeriodStart = moment.unix(invoice.period_start).format('MMM DD, YYYY');
    const billingPeriodEnd = moment.unix(invoice.period_end).format('MMM DD, YYYY');
    const billingPeriod = {
      start: billingPeriodStart,
      end: billingPeriodEnd,
    };
    return {...billingRecord, billingPeriod} as ApiImageryPurchaseBillingRecord & {
      billingPeriod: imageryUtils.BillingPeriod;
    };
  });

  return purchasesWithBillingPeriods;
};

// Every purchase in one portfolio, with the billing period each was invoiced in.
export async function exportPortfolioOrderHistoryCsv({
  portfolio,
  billingRecords,
  contracts,
  portfolios,
  organizationId,
  api = Remote.api,
}: {
  portfolio: {id: string; name: string};
  billingRecords: ApiImageryPurchaseBillingRecord[];
  contracts: ApiImageryContractWithProjects[];
  portfolios: {id: string; name: string}[];
  organizationId: string;
  api?: Pick<typeof Remote.api, 'organizations'>;
}) {
  imageryUtils.exportRecordsCsv({
    fileName: `Order History ${portfolio.name}`,
    billingRecords: await appendBillingPeriodsToBillingRecords(
      billingRecords.filter((r) => r.projectId === portfolio.id),
      api,
      organizationId
    ),
    contracts,
    projects: portfolios,
  });
}

export type Portfolio = {id: string; name: string};

// The portfolios that have a purchase breakdown to open, by name.
export function portfoliosWithPurchases(
  portfolios: Portfolio[],
  billingRecords: ApiImageryPurchaseBillingRecord[]
): Portfolio[] {
  const purchasedIds = new Set(billingRecords.map((r) => r.projectId));
  return portfolios
    .filter((p) => purchasedIds.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type PortfolioSummary = {
  id: string;
  name: string;
  firstOrder: string | null;
  lastOrder: string | null;
  scenes: number;
  acres: number;
  spentCents: number;
};

const SUMMARY_HEADERS = [
  'Portfolio',
  'First Order',
  'Last Order',
  'Scenes',
  'Acreage',
  'Total Spent (USD)',
];

// One row per portfolio, as the Purchases tab shows it.
export function exportPortfolioSummaryCsv(summaries: PortfolioSummary[]) {
  new ExportToCsv({
    showLabels: true,
    useBom: false,
    filename: 'Purchases by Portfolio',
    headers: SUMMARY_HEADERS,
  }).generateCsv(
    summaries.map((s) => ({
      Portfolio: s.name,
      'First Order': s.firstOrder ? imageryUtils.formatDate(s.firstOrder) : '',
      'Last Order': s.lastOrder ? imageryUtils.formatDate(s.lastOrder) : '',
      Scenes: s.scenes,
      Acreage: s.acres,
      'Total Spent (USD)': (s.spentCents / 100).toFixed(2),
    }))
  );
}

export const FIRST_ROWS = 5;
export const ROWS_PER_PAGE = 50;
